import type {
	SuggestedCommentReplyAgentCommentType,
	SuggestedCommentReplyCommentTypes,
	SuggestedCommentReplyConvoAgentProduct,
} from '@atlassian/jira-ai-assistance-service-client/src/services/invoke-agent/suggested-comment-reply-agent/types.tsx';

type ConvoAIResponse = {
	ai_feature_output?: {
		commentSuggestions?: ConvoAICommentSuggestion[];
	};
};

type ConvoAICommentSuggestion = {
	commentType: SuggestedCommentReplyAgentCommentType;
	text: string;
	adf: object | null;
};

// The return type for transformComments
type CommentSuggestion = {
	commentReplyType: SuggestedCommentReplyAgentCommentType;
	text: string;
	adf: object | null;
};

/**
 * Calls the Convo-AI API for suggested comment replies.
 * @param agentRequest - { object_id, locale, product, comment_types, adf_format? }
 * @returns {Promise<{ content: { commentSuggestions: CommentSuggestion[] } }>} - compatible with transformComments
 */
export const convoAIAgent = async (
	agentRequest: {
		object_id: string;
		locale: string;
		product: SuggestedCommentReplyConvoAgentProduct;
		comment_types: SuggestedCommentReplyCommentTypes;
		adf_format?: boolean;
	},
	signal?: AbortSignal,
): Promise<{ content: { commentSuggestions: CommentSuggestion[] } }> => {
	const response = await fetch('/gateway/api/assist/ai/v2/ai-feature/suggested-comment-reply', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-Product': 'jira',
			'X-Experience-Id': 'suggested-comment-reply',
		},
		body: JSON.stringify({
			ai_feature_input: {
				object_id: agentRequest.object_id,
				locale: agentRequest.locale,
				product: agentRequest.product,
				comment_type: agentRequest.comment_types,
				adf_format: agentRequest.adf_format ?? false,
			},
		}),
		signal,
	});

	if (!response.ok) {
		throw new Error(`Convo-AI API failed: ${response.status}`);
	}

	const result: ConvoAIResponse = await response.json();

	let commentSuggestions: CommentSuggestion[] = [];
	if (result.ai_feature_output?.commentSuggestions) {
		commentSuggestions = result.ai_feature_output.commentSuggestions.map((item) => ({
			commentReplyType: item.commentType,
			text: item.text,
			adf: item.adf,
		}));
	}

	return { content: { commentSuggestions } };
};
